import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { UserSettingType } from 'shared/interfaces/user-settings/user-setting-type.enum';
import {
  INVITE_MENU_CONTENT_LOADED_EVENT,
  INVITE_NOTIFICATIONS_REFRESH_INTERVAL_MS,
  INVITE_NOTIFICATIONS_UPDATED_EVENT,
} from 'src/constants/invite-notifications';
import type { InviteMenuName } from 'src/constants/invite-notifications';
import { useUserSettingsContext } from 'src/contexts/UserSettingsContext/UserSettingsContext';

const normalizeCount = (count: number | null | undefined): number =>
  Number.isFinite(count) && Number(count) > 0 ? Math.floor(Number(count)) : 0;

export function useInviteMenuNotifications() {
  const location = useLocation();
  const { userSettings, userSettingsLoaded, saveUserSettings, refreshUserSettings } = useUserSettingsContext();
  const isGamesPage = location.pathname.includes('/games');
  const isSurveysPage = location.pathname.includes('/surveys');
  const unreadGameInviteCount = normalizeCount(userSettings?.unreadGameInviteCount);
  const unreadProjectInviteCount = normalizeCount(userSettings?.unreadProjectInviteCount);
  const previousPageRef = useRef({ initialized: false, isGamesPage: false, isSurveysPage: false });

  useEffect(() => {
    if (!userSettingsLoaded) {
      return;
    }

    const enteredGamesPage =
      isGamesPage && (!previousPageRef.current.initialized || !previousPageRef.current.isGamesPage);
    const enteredSurveysPage =
      isSurveysPage && (!previousPageRef.current.initialized || !previousPageRef.current.isSurveysPage);

    if (enteredGamesPage && unreadGameInviteCount > 0) {
      void saveUserSettings(UserSettingType.UnreadGameInviteCount, '0');
    }

    if (enteredSurveysPage && unreadProjectInviteCount > 0) {
      void saveUserSettings(UserSettingType.UnreadProjectInviteCount, '0');
    }

    previousPageRef.current = { initialized: true, isGamesPage, isSurveysPage };
  }, [
    isGamesPage,
    isSurveysPage,
    saveUserSettings,
    unreadGameInviteCount,
    unreadProjectInviteCount,
    userSettingsLoaded,
  ]);

  useEffect(() => {
    const markMenuContentAsSeen = (event: Event) => {
      const menu = (event as CustomEvent<{ menu?: InviteMenuName }>).detail?.menu;
      const seenAt = new Date().toISOString();

      if (menu === 'games' && isGamesPage) {
        void saveUserSettings(UserSettingType.UnreadGameInviteCount, '0');
        void saveUserSettings(UserSettingType.LastGameInviteSeenAt, seenAt);
      }

      if (menu === 'projects' && isSurveysPage) {
        void saveUserSettings(UserSettingType.UnreadProjectInviteCount, '0');
        void saveUserSettings(UserSettingType.LastProjectInviteSeenAt, seenAt);
      }
    };

    window.addEventListener(INVITE_MENU_CONTENT_LOADED_EVENT, markMenuContentAsSeen);

    return () => {
      window.removeEventListener(INVITE_MENU_CONTENT_LOADED_EVENT, markMenuContentAsSeen);
    };
  }, [isGamesPage, isSurveysPage, saveUserSettings]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshUserSettings();
      }
    };

    const intervalId = window.setInterval(refreshWhenVisible, INVITE_NOTIFICATIONS_REFRESH_INTERVAL_MS);
    window.addEventListener('focus', refreshWhenVisible);
    window.addEventListener(INVITE_NOTIFICATIONS_UPDATED_EVENT, refreshWhenVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshWhenVisible);
      window.removeEventListener(INVITE_NOTIFICATIONS_UPDATED_EVENT, refreshWhenVisible);
    };
  }, [refreshUserSettings]);

  return {
    unreadGameInviteCount: isGamesPage ? 0 : unreadGameInviteCount,
    unreadProjectInviteCount: isSurveysPage ? 0 : unreadProjectInviteCount,
  };
}
